import { useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Skeleton, Tooltip } from 'antd';
import {
  PushpinOutlined,
  PushpinFilled,
  CaretRightOutlined,
  CaretDownOutlined,
  LeftOutlined,
  RightOutlined,
  ShopOutlined,
  GlobalOutlined,
  SwapOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useStore } from '@/stores';
import type { ResolvedModule } from '@/stores/StudioModuleStore';
import { colors } from '@/theme/tokens';
import { HEALTH_STATUS_META } from '@/features/system/healthStatus';
import { HOME_KEY, destinationByKey, moduleGlyph, type Destination } from './navigation';

// The module rail. It shows Home, then ONE module's destinations — the module that
// owns the current URL (or, on a page in no module, the one last used) — then the
// person's pins. The other modules live behind the header's Modules launcher; they
// are never listed here. Everything comes from StudioModuleStore's resolved model,
// so a destination this person may not open, or one in a hidden module, never shows.
//
// Width is 200px (labels) or 60px (icons) — the provider-web geometry — decided by
// NavStore's wide slot (the rail-slot state machine); `forceSlim` overrides it on
// narrow screens.

export const RAIL_WIDE = 200;
export const RAIL_SLIM = 60;

interface NavRailProps {
  module: ResolvedModule | undefined;
  activeKey: string;
  isDark: boolean;
  merchantSelected: boolean;
  forceSlim?: boolean;
  onOpenLauncher: () => void;
  topCard: React.ReactNode;
  contextCard?: React.ReactNode;
  footer?: React.ReactNode;
}

export const NavRail = observer(function NavRail({
  module,
  activeKey,
  isDark,
  merchantSelected,
  forceSlim,
  onOpenLauncher,
  topCard,
  contextCard,
  footer,
}: NavRailProps) {
  const { navStore, studioModuleStore: sm } = useStore();
  const wide = !forceSlim && navStore.isWide('left');
  const border = isDark ? colors.slate[700] : colors.slate[200];
  const home = destinationByKey(HOME_KEY)!;

  // Pins are preferences: only those the resolved navigation still offers this person.
  const pinned = sm.discoverable.filter((d) => navStore.isPinned(d.destination.key));

  return (
    <aside
      aria-label="Module navigation"
      style={{
        width: wide ? RAIL_WIDE : RAIL_SLIM,
        flexShrink: 0,
        background: isDark ? colors.slate[900] : '#FFFFFF',
        borderRight: `1px solid ${border}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'width 140ms ease',
        minHeight: 0,
      }}
    >
      {!forceSlim && <RailChevron wide={wide} isDark={isDark} />}
      {topCard}

      <nav aria-label={module ? `${module.title} pages` : 'Pages'} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: wide ? '2px 8px 12px' : '2px 6px 12px' }}>
        <NavRow item={home} title="Home" active={activeKey === HOME_KEY} wide={wide} isDark={isDark} disabled={false} pinnable={false} />

        {sm.status === 'loading' && (
          <div style={{ padding: wide ? '14px 10px' : '14px 4px' }} aria-busy="true" aria-label="Loading your modules">
            <Skeleton active title={false} paragraph={{ rows: 5, width: wide ? ['70%', '85%', '60%', '80%', '65%'] : ['100%', '100%', '100%', '100%', '100%'] }} />
          </div>
        )}

        {sm.status === 'error' && (
          <div role="alert" style={{ padding: wide ? '12px 10px' : '12px 2px', fontSize: 12, lineHeight: 1.5, color: isDark ? colors.slate[300] : colors.slate[600] }}>
            {wide && <div style={{ marginBottom: 8 }}>Couldn't load your modules. Pages you open directly still work.</div>}
            <Tooltip title={wide ? null : "Couldn't load your modules — retry"} placement="right">
              <Button size="small" icon={<ReloadOutlined />} onClick={() => void sm.load()} aria-label="Retry loading modules" block={wide}>
                {wide ? 'Retry' : null}
              </Button>
            </Tooltip>
          </div>
        )}

        {sm.isStale && (
          <div role="status" style={{ padding: wide ? '8px 10px' : '8px 2px', fontSize: 12, lineHeight: 1.5, color: isDark ? colors.amber[100] : colors.amber[700] }}>
            {wide && <div style={{ marginBottom: 6 }}>Menu may be out of date — couldn't refresh it.</div>}
            <Tooltip title={wide ? null : "Menu may be out of date — retry refreshing it"} placement="right">
              <Button size="small" icon={<ReloadOutlined />} loading={sm.refreshing} onClick={() => void sm.load()} aria-label="Retry refreshing modules" block={wide}>
                {wide ? 'Retry' : null}
              </Button>
            </Tooltip>
          </div>
        )}

        {sm.status === 'ready' && !module && (
          wide && (
            <div style={{ padding: '12px 10px', fontSize: 12, lineHeight: 1.5, color: isDark ? colors.slate[400] : colors.slate[500] }}>
              No modules are available to you yet.
            </div>
          )
        )}

        {module && (
          <>
            <ModuleHeader module={module} wide={wide} isDark={isDark} onSwitch={onOpenLauncher} />
            <div id={`rail-${module.key}`} hidden={navStore.isCollapsed(`module:${module.key}`) && wide}>
              {(!wide || !navStore.isCollapsed(`module:${module.key}`)) &&
                module.items.map((it) => (
                  <NavRow key={it.key} item={it} active={it.key === activeKey} wide={wide} isDark={isDark} disabled={isLocked(it, merchantSelected)} pinnable />
                ))}
            </div>
          </>
        )}

        {pinned.length > 0 && (
          <>
            <GroupHeader id="pinned" label="Pinned" wide={wide} isDark={isDark} />
            {!navStore.isCollapsed('pinned') &&
              pinned.map(({ destination, module: owner }) => (
                <NavRow
                  key={`pin-${destination.key}`}
                  item={destination}
                  moduleTitle={owner.title}
                  active={destination.key === activeKey}
                  wide={wide}
                  isDark={isDark}
                  disabled={isLocked(destination, merchantSelected)}
                  pinnable
                />
              ))}
          </>
        )}

        {module && !merchantSelected && wide && module.items.some((i) => i.lockWithoutMerchant) && (
          <div style={{ padding: '10px 12px 0', fontSize: 12, lineHeight: 1.5, color: isDark ? colors.slate[400] : colors.slate[500] }}>
            Pick a merchant above to open this module's merchant pages.
          </div>
        )}
      </nav>

      {contextCard}
      {footer}
    </aside>
  );
});

/** Without a merchant, pages that load one merchant's data have nothing to show, so they are greyed (never hidden). */
function isLocked(item: Destination, merchantSelected: boolean): boolean {
  return !merchantSelected && !!item.lockWithoutMerchant;
}

// ── Module header ────────────────────────────────────────────────────────────

const ModuleHeader = observer(function ModuleHeader({
  module,
  wide,
  isDark,
  onSwitch,
}: {
  module: ResolvedModule;
  wide: boolean;
  isDark: boolean;
  onSwitch: () => void;
}) {
  const { navStore } = useStore();
  const id = `module:${module.key}`;
  const collapsed = navStore.isCollapsed(id);
  const tile = (
    <span
      aria-hidden
      style={{
        width: 22,
        height: 22,
        borderRadius: 7,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        color: '#FFFFFF',
        fontSize: 12,
        background: module.color ?? colors.slate[500],
      }}
    >
      {moduleGlyph(module.iconKey)}
    </span>
  );

  if (!wide) {
    return (
      <Tooltip title={`${module.title} — switch module`} placement="right">
        <button
          type="button"
          onClick={onSwitch}
          aria-label={`Current module: ${module.title}. Switch module`}
          style={{ display: 'grid', placeItems: 'center', width: '100%', margin: '12px 0 6px', padding: '6px 0', border: 'none', background: 'transparent', cursor: 'pointer' }}
        >
          {tile}
        </button>
      </Tooltip>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '14px 4px 4px 6px' }}>
      <button
        type="button"
        aria-expanded={!collapsed}
        aria-controls={`rail-${module.key}`}
        onClick={() => navStore.toggleSection(id)}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          fontFamily: 'inherit',
          color: isDark ? colors.slate[200] : colors.slate[700],
          fontSize: 12,
          fontWeight: 700,
          textAlign: 'left',
        }}
      >
        {tile}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{module.title}</span>
        {collapsed ? <CaretRightOutlined style={{ fontSize: 9 }} /> : <CaretDownOutlined style={{ fontSize: 9 }} />}
      </button>
      <Tooltip title="Switch module" placement="right">
        <Button type="text" size="small" icon={<SwapOutlined />} onClick={onSwitch} aria-label="Switch module" />
      </Tooltip>
    </div>
  );
});

// ── Chevron ──────────────────────────────────────────────────────────────────

const RailChevron = observer(function RailChevron({ wide, isDark }: { wide: boolean; isDark: boolean }) {
  const { navStore } = useStore();
  return (
    <Tooltip title={wide ? 'Collapse to icons' : 'Expand'} placement="right">
      <button
        type="button"
        aria-label={`${wide ? 'Collapse' : 'Expand'} the module menu`}
        aria-expanded={wide}
        onClick={() => navStore.toggleRail('left')}
        style={{
          position: 'absolute',
          top: 74,
          right: -11,
          width: 22,
          height: 22,
          borderRadius: 999,
          border: `1px solid ${isDark ? colors.slate[600] : colors.slate[300]}`,
          background: isDark ? colors.slate[800] : '#FFFFFF',
          color: isDark ? colors.slate[300] : colors.slate[500],
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
          zIndex: 5,
          padding: 0,
          fontSize: 10,
          boxShadow: '0 2px 6px rgba(15,23,42,0.08)',
        }}
      >
        {wide ? <LeftOutlined /> : <RightOutlined />}
      </button>
    </Tooltip>
  );
});

// ── Group header ─────────────────────────────────────────────────────────────

const GroupHeader = observer(function GroupHeader({ id, label, wide, isDark }: { id: string; label: string; wide: boolean; isDark: boolean }) {
  const { navStore } = useStore();
  if (!wide) {
    // Icon strip: a hairline keeps the groups apart without a label to read.
    return <div aria-hidden style={{ height: 1, margin: '10px 14px 6px', background: isDark ? colors.slate[700] : colors.slate[200] }} />;
  }
  const collapsed = navStore.isCollapsed(id);
  return (
    <button
      type="button"
      aria-expanded={!collapsed}
      onClick={() => navStore.toggleSection(id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        padding: '12px 10px 4px',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: isDark ? colors.slate[400] : colors.slate[500],
        cursor: 'pointer',
        userSelect: 'none',
        border: 'none',
        background: 'transparent',
        fontFamily: 'inherit',
      }}
    >
      {collapsed ? <CaretRightOutlined style={{ fontSize: 9 }} /> : <CaretDownOutlined style={{ fontSize: 9 }} />}
      {label}
    </button>
  );
});

// ── Row ──────────────────────────────────────────────────────────────────────

const NavRow = observer(function NavRow({
  item,
  title,
  moduleTitle,
  active,
  wide,
  isDark,
  disabled,
  pinnable,
}: {
  item: Destination;
  title?: string;
  /** Set on pins: the module the destination belongs to (opening it switches there). */
  moduleTitle?: string;
  active: boolean;
  wide: boolean;
  isDark: boolean;
  disabled: boolean;
  pinnable: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const { navStore, systemHealthStore } = useStore();
  const isPinned = navStore.isPinned(item.key);
  const label = title ?? item.label;

  const accent = colors.ink[600];
  const activeColor = isDark ? colors.ink[100] : accent;
  const activeBg = isDark ? accent + '33' : colors.ink[50];
  const hoverBg = isDark ? colors.slate[800] : colors.slate[100];
  const fg = active ? activeColor : isDark ? colors.slate[300] : colors.slate[700];

  const health = item.live === 'system-health' ? HEALTH_STATUS_META[systemHealthStore.status] : null;
  // Scope cues: platform pages say so; a merchant page that is not greyed without a merchant says it needs one.
  const platformMark = item.scope === 'platform' || item.scope === 'both';
  const merchantMark = item.scope === 'merchant' && !item.lockWithoutMerchant && item.key !== HOME_KEY;
  const scopeWords = item.scope === 'platform' ? 'whole platform' : item.scope === 'both' ? 'platform default and this merchant' : null;

  const tip = disabled
    ? `${label} — pick a merchant first`
    : !wide
      ? `${moduleTitle ? `${moduleTitle} · ` : ''}${label}${health ? ` — ${health.label}` : ''}${scopeWords ? ` (${scopeWords})` : ''}${merchantMark ? ' (this merchant)' : ''}`
      : moduleTitle
        ? `In ${moduleTitle}`
        : null;

  const row = (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: wide ? 'flex-start' : 'center',
        gap: 10,
        padding: wide ? '6px 10px' : '8px 0',
        margin: '1px 0',
        borderRadius: 8,
        background: active ? activeBg : (hovered || focused) && !disabled ? hoverBg : 'transparent',
        color: fg,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        lineHeight: 1.4,
        transition: 'background 80ms ease, color 80ms ease',
      }}
    >
      <span style={{ display: 'inline-flex', width: 18, justifyContent: 'center', fontSize: wide ? 14 : 16, color: active ? activeColor : isDark ? colors.slate[400] : colors.slate[500], position: 'relative' }}>
        {item.icon}
        {health && !wide && <StatusDot color={health.color} style={{ position: 'absolute', top: -2, right: -4 }} ring={isDark ? colors.slate[900] : '#FFFFFF'} />}
      </span>
      {wide && (
        <>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          {pinnable && !disabled && (hovered || focused || isPinned) && (
            <Tooltip title={isPinned ? 'Unpin' : 'Pin'} placement="right">
              <button
                type="button"
                aria-label={isPinned ? `Unpin ${label}` : `Pin ${label}`}
                aria-pressed={isPinned}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navStore.togglePin(item.key);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  padding: 0,
                  border: 'none',
                  borderRadius: 6,
                  background: 'transparent',
                  color: isPinned ? colors.amber[600] : isDark ? colors.slate[400] : colors.slate[500],
                  cursor: 'pointer',
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                {isPinned ? <PushpinFilled /> : <PushpinOutlined />}
              </button>
            </Tooltip>
          )}
          {merchantMark && (
            <Tooltip title="Shows the selected merchant only" placement="right">
              <ShopOutlined aria-label="this merchant" style={{ fontSize: 12, color: colors.ink[500], flexShrink: 0 }} />
            </Tooltip>
          )}
          {platformMark && (
            <Tooltip title={item.scope === 'both' ? 'Platform default and the selected merchant' : 'Whole platform, whichever merchant is selected'} placement="right">
              <GlobalOutlined aria-label={scopeWords ?? 'platform'} style={{ fontSize: 11.5, color: colors.violet[600], flexShrink: 0 }} />
            </Tooltip>
          )}
          {health && (
            <Tooltip title={healthTip(health.label, systemHealthStore.checkedAt, systemHealthStore.error)} placement="right">
              <StatusDot color={health.color} />
            </Tooltip>
          )}
        </>
      )}
      {active && (
        <span aria-hidden style={{ position: 'absolute', left: 0, top: 7, bottom: 7, width: 3, borderRadius: 999, background: activeColor }} />
      )}
    </div>
  );

  const body = disabled ? (
    <div aria-disabled="true" role="link" tabIndex={0} aria-label={`${label} — pick a merchant first`}>{row}</div>
  ) : (
    <Link
      to={item.to}
      style={{ textDecoration: 'none', display: 'block', borderRadius: 8 }}
      aria-current={active ? 'page' : undefined}
      aria-label={wide ? undefined : label}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      // A click inside a 60px rail asks to see labels (provider-web rule).
      onClick={wide ? undefined : () => navStore.claimRail('left')}
    >
      {row}
    </Link>
  );

  return tip ? (
    <Tooltip title={tip} placement="right" mouseEnterDelay={0.15}>
      {body}
    </Tooltip>
  ) : (
    body
  );
});

function StatusDot({ color, style, ring }: { color: string; style?: React.CSSProperties; ring?: string }) {
  return (
    <span
      aria-hidden
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        background: color,
        flexShrink: 0,
        boxShadow: ring ? `0 0 0 2px ${ring}` : undefined,
        ...style,
      }}
    />
  );
}

function healthTip(label: string, checkedAt: number | null, error: string | null): string {
  if (error) return "Couldn't reach the health check — status unknown";
  if (checkedAt == null) return 'Checking…';
  const secs = Math.max(0, Math.round((Date.now() - checkedAt) / 1000));
  return `${label} · checked ${secs < 5 ? 'just now' : secs < 60 ? `${secs}s ago` : `${Math.round(secs / 60)}m ago`}`;
}
